import React, { useEffect, useState } from "react";

import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router";

import { apiBaseUrls } from "@platform/constants/shared-constants";
import { eventBus } from "@platform/events/EventBus";
import { StateEvent } from "@platform/state/types";
import debugLogger from "@platform/utils/debugLogger";

import { AUTH, ERROR, ORGANIZATIONS } from "../../../platform/constants/routes";

/** Response shape of GET /api/v1/auth/me */
interface AuthMeResponse {
  id: number;
  email: string;
  permissions: string[];
  orgId: number | null;
  orgAlias: string | null;
  /** 1 = authenticated but no org selected; 2 = org-scoped session */
  step: 1 | 2;
}

interface RecoveryStep {
  id: string;
  name: string;
  description: string;
  status: "pending" | "checking" | "completed" | "failed";
  result?: string;
}

interface RecoveryPageProps {
  /** Optional injected event bus. Defaults to the singleton platform eventBus. */
  eventBus?: typeof eventBus;
}

export const RecoveryPage: React.FC<RecoveryPageProps> = ({
  eventBus: bus = eventBus,
}) => {
  const navigate = useNavigate();
  const [overallProgress, setOverallProgress] = useState(0);
  const [recoverySteps, setRecoverySteps] = useState<RecoveryStep[]>([
    {
      id: "session",
      name: "Session Check",
      description: "Checking for existing user session",
      status: "pending",
    },
    {
      id: "auth",
      name: "Authentication",
      description: "Validating session cookie via /auth/me",
      status: "pending",
    },
    {
      id: "organizations",
      name: "Organization Context",
      description: "Checking session organization scope",
      status: "pending",
    },
  ]);
  const [recovered, setRecovered] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateStep = (
    stepId: string,
    status: RecoveryStep["status"],
    result?: string,
  ) => {
    setRecoverySteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, status, result } : step,
      ),
    );
  };

  useEffect(() => {
    const runRecovery = async () => {
      try {
        setError(null);

        // Step 1: session check
        updateStep("session", "checking");
        setOverallProgress(10);

        // Step 2: call /auth/me
        updateStep("auth", "checking");
        setOverallProgress(30);

        const response = await fetch(`${apiBaseUrls.BACKEND}/api/v1/auth/me`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        setOverallProgress(70);

        if (response.status === 401) {
          // No session cookie / expired
          updateStep("session", "failed", "No active session");
          updateStep("auth", "failed", "Session cookie missing or expired");
          updateStep("organizations", "failed", "No organization context");
          setOverallProgress(100);
          setRecovered(false);

          bus.emitGlobal(StateEvent.AUTH_DATA_MISSING, {
            timestamp: new Date().toISOString(),
            context: "recovery",
          });
          // Brief delay so the user sees the final state before redirect
          setTimeout(() => {
            void navigate(AUTH.LOGIN.path, { replace: true });
          }, 800);
          return;
        }

        if (!response.ok) {
          // Unexpected error (5xx, etc.)
          updateStep("session", "failed", "Session check failed");
          updateStep("auth", "failed", `Server returned ${response.status}`);
          updateStep("organizations", "failed", "Unable to determine context");
          setOverallProgress(100);
          setError(`Session check failed (HTTP ${response.status})`);

          bus.emitGlobal(StateEvent.ERROR_OCCURRED, {
            error: `Session check failed (HTTP ${response.status})`,
            context: "recovery",
            timestamp: new Date().toISOString(),
          });
          setTimeout(() => {
            void navigate(ERROR.path, { replace: true });
          }, 1500);
          return;
        }

        // 200 — session is valid
        updateStep("session", "completed", "Active session found");
        updateStep("auth", "completed", "Session validated");

        const data = (await response.json()) as AuthMeResponse;

        if (data.step === 2 && data.orgAlias) {
          // Step-2 token: org context present -> straight to workspace
          updateStep(
            "organizations",
            "completed",
            `Organization: ${data.orgAlias}`,
          );
          setOverallProgress(100);
          setRecovered(true);

          bus.emitGlobal(StateEvent.SESSION_HAS_ORG, {
            orgAlias: data.orgAlias,
            orgId: data.orgId,
            timestamp: new Date().toISOString(),
          });

          const orgPath = ORGANIZATIONS.DETAILS.getPath(data.orgAlias);
          setTimeout(() => {
            void navigate(orgPath, { replace: true });
          }, 800);
          return;
        }

        // step === 1: authenticated but no org selected
        updateStep("organizations", "failed", "No organization selected");
        setOverallProgress(100);
        setRecovered(false);

        bus.emitGlobal(StateEvent.SESSION_NO_ORG, {
          timestamp: new Date().toISOString(),
        });

        setTimeout(() => {
          void navigate(AUTH.LOGIN.path, { replace: true });
        }, 800);
      } catch (err) {
        debugLogger.error("Recovery failed:", err);
        const message =
          err instanceof Error ? err.message : "Unknown error occurred";
        setError(message);
        setOverallProgress(100);

        bus.emitGlobal(StateEvent.ERROR_OCCURRED, {
          error: message,
          context: "recovery",
          timestamp: new Date().toISOString(),
        });

        setTimeout(() => {
          void navigate(ERROR.path, { replace: true });
        }, 1500);
      }
    };

    void runRecovery();
  }, []);

  const getStepIcon = (status: RecoveryStep["status"]) => {
    switch (status) {
      case "completed":
        return "✓";
      case "failed":
        return "✗";
      case "checking":
        return (
          <CircularProgress size={16} aria-label="Checking recovery step" />
        );
      default:
        return "○";
    }
  };

  const getStepColor = (status: RecoveryStep["status"]) => {
    switch (status) {
      case "completed":
        return "success.main";
      case "failed":
        return "error.main";
      case "checking":
        return "primary.main";
      default:
        return "text.secondary";
    }
  };

  const handleManualLogin = () => {
    void navigate(AUTH.LOGIN.path);
  };

  const handleRetry = () => {
    setOverallProgress(0);
    setRecovered(null);
    setError(null);
    setRecoverySteps((prev) =>
      prev.map((step) => ({
        ...step,
        status: "pending" as const,
        result: undefined,
      })),
    );
    // Re-trigger the effect by reloading the page section.
    // The effect runs once on mount; a retry re-mounts via a key bump is the
    // cleanest approach without duplicating the recovery logic.
    window.location.reload();
  };

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 600,
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Session Recovery
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Checking your authentication status
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Overall Progress */}
        <Box sx={{ width: "100%", mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Recovery Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(overallProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            aria-label="Recovery progress"
            aria-valuetext={`${Math.round(overallProgress)}% complete`}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        {/* Recovery Steps */}
        <Card sx={{ width: "100%", mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recovery Steps
            </Typography>

            {recoverySteps.map((step) => (
              <Box
                key={step.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                {/* Step Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    mr: 2,
                    color: getStepColor(step.status),
                  }}
                >
                  {getStepIcon(step.status)}
                </Box>

                {/* Step Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {step.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                  {step.result && (
                    <Typography
                      variant="caption"
                      color={
                        step.status === "completed"
                          ? "success.main"
                          : "error.main"
                      }
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {step.result}
                    </Typography>
                  )}
                </Box>

                {/* Step Status */}
                <Typography
                  variant="caption"
                  color={getStepColor(step.status)}
                  sx={{ textTransform: "capitalize" }}
                >
                  {step.status}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Result Display */}
        {recovered !== null && (
          <Card sx={{ width: "100%", mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recovery Result
              </Typography>
              {recovered ? (
                <Alert severity="success">
                  Session recovered successfully! Redirecting to workspace...
                </Alert>
              ) : (
                <Alert severity="info">
                  No valid session found. Please log in to continue.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {overallProgress === 100 && recovered === false && !error && (
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleManualLogin}
              size="large"
            >
              Log In
            </Button>
            <Button variant="outlined" onClick={handleRetry} size="large">
              Retry Recovery
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecoveryPage;
